/*
 * DEINSPAR – Cm / In (Storefront only)
 * The real Shopify variant, SKU, size, price and checkout remain in cm.
 * This script changes only elements explicitly marked with
 * data-deinspar-size-unit-label and stores a visitor's display preference.
 */
(function () {
  'use strict';
  if (window.DeinsparSizeUnits) return;

  const KEY = 'deinspar:size-unit:v1';
  const TEXT = '[data-deinspar-size-unit-label]';
  const TOGGLE = '[data-deinspar-size-unit]';
  let unit = 'cm';
  try {
    if (localStorage.getItem(KEY) === 'in') unit = 'in';
  } catch (_) { /* Private browsing – use current-page preference */ }

  // All values displayed are integer cm (including legacy "cm (in)" values).
  // Do not attempt to transform arbitrary product option labels or names.
  function dimensions(raw) {
    const match = String(raw ?? '').match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm\b/i);
    if (!match) return null;
    const width = Number(match[1].replace(',', '.'));
    const height = Number(match[2].replace(',', '.'));
    if (!(width > 0 && height > 0 && width < 10000 && height < 10000)) return null;
    return { width, height };
  }

  function formatNumber(n) {
    return Number.isInteger(n) ? String(n) : String(n).replace('.', ',');
  }

  function format(raw, unitOverride) {
    const cm = dimensions(raw);
    if (!cm) return String(raw ?? '');
    const selectedUnit = unitOverride === 'cm' || unitOverride === 'in' ? unitOverride : unit;
    if (selectedUnit === 'in') {
      return `${Math.round(cm.width / 2.54)}" x ${Math.round(cm.height / 2.54)}" in`;
    }
    return `${formatNumber(cm.width)} × ${formatNumber(cm.height)} cm`;
  }

  // The normal product's trigger text is overwritten by main-product.js when
  // the selected variant changes. Always derive its true cm value from the
  // size option's data-custom-size-value; never reuse formatted "in" text.
  function rawValue(node) {
    if (node.hasAttribute('data-custom-size-trigger-text')) {
      const picker = node.closest('[data-custom-size-picker]');
      const selected = picker?.querySelector('.custom-size-option.is-active[data-custom-size-value]') ||
        picker?.querySelector('.custom-size-sheet-option.is-active[data-custom-size-value]');
      if (selected) return selected.getAttribute('data-custom-size-value');
    }
    return node.getAttribute('data-deinspar-size-raw');
  }

  function refresh(root) {
    const scope = root || document;
    const nodes = [];
    if (scope.nodeType === 1 && scope.matches(TEXT)) nodes.push(scope);
    scope.querySelectorAll?.(TEXT).forEach(node => nodes.push(node));
    nodes.forEach(node => {
      const raw = rawValue(node);
      if (!raw || !dimensions(raw)) return;
      const shown = format(raw);
      if (node.textContent !== shown) node.textContent = shown;
    });
    document.querySelectorAll(TOGGLE).forEach(button => {
      const active = button.getAttribute('data-deinspar-size-unit') === unit;
      if (button.getAttribute('aria-pressed') !== String(active)) {
        button.setAttribute('aria-pressed', String(active));
      }
    });
  }

  function setUnit(next) {
    if (next !== 'cm' && next !== 'in') return;
    unit = next;
    try { localStorage.setItem(KEY, next); } catch (_) { /* optional */ }
    refresh();
  }

  window.DeinsparSizeUnits = { format, refresh, setUnit, getUnit: () => unit };

  document.addEventListener('click', function (event) {
    const button = event.target.closest?.(TOGGLE);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    setUnit(button.getAttribute('data-deinspar-size-unit'));
  });

  let pending = false;
  function queueRefresh() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      refresh();
    });
  }

  function relevantMutation(record) {
    if (record.type !== 'childList') return false;
    const el = record.target.nodeType === 1 ? record.target : record.target.parentElement;
    if (el?.closest?.('[data-custom-size-picker], .minicart__product-info, #main-cart-items')) {
      return true;
    }
    // AJAX may replace the entire mini-cart or cart section.
    return Array.from(record.addedNodes).some(node =>
      node.nodeType === 1 && (node.matches?.(TEXT) || node.querySelector?.(TEXT))
    );
  }

  function boot() {
    refresh();
    const observer = new MutationObserver(mutations => {
      if (mutations.some(relevantMutation)) queueRefresh();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', queueRefresh);
    window.addEventListener('pageshow', queueRefresh);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
